"use client";

import React from "react";
import type { TemplateData } from "../../types";
import { AppointmentNote } from "./AppointmentNote";
import { ICalendar } from "./ICalendar";

type ReviewData = NonNullable<TemplateData["appointmentReview"]>;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const pageStyle: React.CSSProperties = {
  color: "#1a1a1a",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 0,
};

const colLeftStyle: React.CSSProperties = {
  paddingBottom: 24,
};

const colRightStyle: React.CSSProperties = {
  paddingTop: 24,
  borderTop: "1px solid #e8e8e8",
};

const h2Style: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: "#000",
  margin: "0 0 8px",
  lineHeight: 1.25,
};

const bodyStyle: React.CSSProperties = {
  color: "#444",
  fontSize: 14,
  lineHeight: 1.7,
  margin: "0 0 8px",
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: "#000",
  marginBottom: 16,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
};

const sectionTitleSmStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: "#000",
  marginBottom: 8,
};

const detailItemStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  padding: "10px 0",
  borderBottom: "1px solid #f0f0f0",
};

const detailLabelStyle: React.CSSProperties = {
  color: "#999",
  fontSize: 13,
  fontWeight: 500,
  minWidth: 80,
};

const detailValueStyle: React.CSSProperties = {
  color: "#1a1a1a",
  fontSize: 14,
  fontWeight: 500,
  textAlign: "right" as const,
};

const detailItemStackedStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "10px 0",
  borderBottom: "1px solid #f0f0f0",
};

const descriptionTextStyle: React.CSSProperties = {
  color: "#1a1a1a",
  fontSize: 14,
  lineHeight: 1.6,
  margin: 0,
  whiteSpace: "pre-line" as const,
};

const listRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  padding: "6px 0",
};

const listRowLabelStyle: React.CSSProperties = {
  color: "#1a1a1a",
  fontSize: 14,
};

const listRowValueStyle: React.CSSProperties = {
  color: "#666",
  fontSize: 13,
  fontWeight: 500,
};

const cardCompactStyle: React.CSSProperties = {
  background: "#fafafa",
  borderRadius: 8,
  padding: "16px 20px",
};

const btnStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "8px 20px",
  fontSize: 13,
  fontWeight: 600,
  color: "#fff",
  backgroundColor: "#000",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  marginTop: 8,
};

const bannerStyle: React.CSSProperties = {
  background: "#1a1a1a",
  color: "#fff",
  textAlign: "center" as const,
  padding: "14px 16px",
  fontSize: 15,
  fontWeight: 500,
  letterSpacing: "0.01em",
  marginBottom: 16,
};

// ---------------------------------------------------------------------------
// Responsive grid via media query class
// ---------------------------------------------------------------------------

const RESPONSIVE_CSS = `
@media (min-width: 768px) {
  .openings-review-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 48px !important;
    align-items: start !important;
  }
  .openings-review-col-left {
    padding-bottom: 0 !important;
    padding-right: 24px !important;
    border-right: 1px solid #e8e8e8 !important;
  }
  .openings-review-col-right {
    padding-top: 0 !important;
    border-top: none !important;
  }
}
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppointmentReviewPage({ data }: { data: ReviewData }) {
  const displayName = data.salonName || "Our salon";
  const parking = data.parking;
  const appointmentDateTime = data.datetime?.trim() || "TBD";
  const address = data.address?.trim() || undefined;
  const services = data.services;
  const serviceDescriptions = services
    .map((service) =>
      (service.option?.description || service.description || "").trim(),
    )
    .filter(Boolean);

  // Build calendar event
  const startIso = data.startsAtIso ?? null;
  const endIso = data.endsAtIso ?? null;
  const startDate = startIso ? new Date(startIso) : null;
  const parsedEndDate = endIso ? new Date(endIso) : null;

  const totalServiceMinutes = services.reduce((acc, service) => {
    const optionDuration =
      service.option && typeof service.option.duration === "number"
        ? service.option.duration
        : null;
    const baseDuration =
      typeof service.duration === "number" ? service.duration : null;
    return acc + (optionDuration ?? baseDuration ?? 0);
  }, 0);

  const effectiveEndDate = (() => {
    if (parsedEndDate && !Number.isNaN(parsedEndDate.getTime()))
      return parsedEndDate;
    if (!startDate || Number.isNaN(startDate.getTime())) return null;
    const minutes = totalServiceMinutes > 0 ? totalServiceMinutes : 60;
    return new Date(startDate.getTime() + minutes * 60 * 1000);
  })();

  const startTimeIso =
    startDate && !Number.isNaN(startDate.getTime())
      ? startDate.toISOString()
      : null;
  const endTimeIso =
    effectiveEndDate && !Number.isNaN(effectiveEndDate.getTime())
      ? effectiveEndDate.toISOString()
      : null;

  const serviceSummary = services
    .map((s) => {
      const optionLabel = s.option?.name ? ` (${s.option.name})` : "";
      return s.name ? `${s.name}${optionLabel}` : null;
    })
    .filter((v): v is string => Boolean(v))
    .join(", ");

  const descriptionLines = [
    `Appointment with ${displayName}`,
    appointmentDateTime ? `When: ${appointmentDateTime}` : null,
    serviceSummary ? `Services: ${serviceSummary}` : null,
    address ? `Where: ${address}` : null,
    data.timezone ? `Time zone: ${data.timezone}` : null,
  ].filter((v): v is string => Boolean(v));

  const calendarEvent =
    !data.isCanceled && startTimeIso && endTimeIso
      ? {
          title: `${displayName} appointment`,
          description: descriptionLines.join("\n") || displayName,
          startTime: startTimeIso,
          endTime: endTimeIso,
          location: address ?? "",
        }
      : null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
      <div style={pageStyle}>
        {data.isCanceled && (
          <div style={bannerStyle}>This appointment has been canceled.</div>
        )}

        <div className="openings-review-grid" style={gridStyle}>
          {/* ── Left column: Info & Note ── */}
          <div className="openings-review-col-left" style={colLeftStyle}>
            {!data.isCanceled && (
              <>
                <h2 style={h2Style}>Thank you!</h2>
                {data.cancellationPolicyText
                  ?.split("\n")
                  .map((line: string, i: number) =>
                    line.trim() ? (
                      <p key={i} style={bodyStyle}>
                        {line}
                      </p>
                    ) : null,
                  )}
                {data.refundPolicyText ? (
                  <p style={bodyStyle}>{data.refundPolicyText}</p>
                ) : null}
              </>
            )}

            {parking && (parking.description || parking.link) ? (
              <div style={{ ...cardCompactStyle, marginTop: 16 }}>
                <div style={sectionTitleSmStyle}>Parking</div>
                {parking.description ? <p>{parking.description}</p> : null}
                {parking.link ? (
                  <a href={parking.link} target="_blank" rel="noreferrer">
                    <button style={btnStyle} type="button">
                      View on Google Maps
                    </button>
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* ── Right column: Details ── */}
          <div className="openings-review-col-right" style={colRightStyle}>
            <div style={sectionTitleStyle}>Details</div>

            {data.memberName ? (
              <div style={detailItemStyle}>
                <span style={detailLabelStyle}>With</span>
                <span style={detailValueStyle}>{data.memberName}</span>
              </div>
            ) : null}

            <div style={detailItemStyle}>
              <span style={detailLabelStyle}>When</span>
              <span style={detailValueStyle}>{appointmentDateTime}</span>
            </div>

            {address ? (
              <div style={detailItemStyle}>
                <span style={detailLabelStyle}>Address</span>
                <span style={detailValueStyle}>{address}</span>
              </div>
            ) : null}

            <div style={detailItemStyle}>
              <span style={detailLabelStyle}>Services</span>
              <div>
                {services.map((service) => {
                  const optionPrice = service.option?.price;
                  const hasOptionPrice =
                    typeof optionPrice === "number" && optionPrice > 0;
                  const basePrice = service.price;
                  const amount = hasOptionPrice
                    ? optionPrice
                    : typeof basePrice === "number" && basePrice > 0
                      ? basePrice
                      : null;
                  const price =
                    amount !== null
                      ? `$${amount.toFixed(2)}${
                          !hasOptionPrice && service.up ? "+" : ""
                        }`
                      : null;
                  const optionLabel = service.option?.name
                    ? ` (${service.option.name})`
                    : "";
                  return (
                    <div key={service.id} style={listRowStyle}>
                      <span style={listRowLabelStyle}>
                        {service.name}
                        {optionLabel}
                      </span>
                      {price ? (
                        <>
                          <span style={{ color: "#ccc", margin: "0 8px" }}>
                            ·
                          </span>
                          <span style={listRowValueStyle}>{price}</span>
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {serviceDescriptions.length > 0 ? (
              <div style={detailItemStackedStyle}>
                <span style={detailLabelStyle}>Description</span>
                <div>
                  {serviceDescriptions.map((description, i) => (
                    <p key={`description-${i}`} style={descriptionTextStyle}>
                      {description}
                    </p>
                  ))}
                </div>
              </div>
            ) : null}

            {!data.isCanceled && (
              <div style={{ marginTop: 24 }}>
                <div style={sectionTitleSmStyle}>Note</div>
                <AppointmentNote
                  appointmentId={data.appointmentId}
                  customerId={data.customerId}
                />
              </div>
            )}

            {calendarEvent ? (
              <div style={{ marginTop: 24, paddingTop: 16 }}>
                <div style={sectionTitleStyle}>Save to your calendar</div>
                <ICalendar event={calendarEvent} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
